import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { reorderMenuItem } from 'src/api/reorderMenuItem/reorderMenuItem';
import { reorderItems } from 'src/helpers/reorderItems';
import { reassignDisplayOrders } from 'src/helpers/reassignDisplayOrders';
import { revertOptimisticWrite } from 'src/helpers/revertOptimisticWrite';
import type { IOptimisticWrite } from 'src/helpers/revertOptimisticWrite';
import type { IDropResult } from 'src/helpers/getDropResult';
import type { IMenu } from 'src/types/menu.types';
import { useGatedMutation } from '../useGatedMutation';
import { useSerialQueue } from '../useSerialQueue';
import type { IEnqueue } from '../useSerialQueue';
import { QueryKeys } from '../QueryKeys';

export const INVALIDATE_DEBOUNCE_MS = 1000;

type IReorderVariables = Extract<IDropResult, { kind: 'reorder' }>;

type IReorderContext = IOptimisticWrite<IMenu>;

export const useReorderMenuItem = (menuId: string, enqueue?: IEnqueue) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const ownQueue = useSerialQueue();
  const enqueueRequest = enqueue ?? ownQueue;
  const invalidateTimer = useRef<ReturnType<typeof setTimeout>>();
  const queryKey = useMemo(() => [QueryKeys.menu, menuId], [menuId]);

  useEffect(() => () => clearTimeout(invalidateTimer.current), []);

  const isPositionCurrent = ({ sectionId, itemId, fromIndex }: IReorderVariables): boolean => {
    const menu = queryClient.getQueryData<IMenu>(queryKey);
    const section = menu?.sections.find((candidate) => candidate.sectionId === sectionId);

    return section?.items[fromIndex]?.itemId === itemId;
  };

  return useGatedMutation<unknown, Error, IReorderVariables, IReorderContext>({
    isInstructionCurrent: isPositionCurrent,

    mutationFn: ({ itemId, newDisplayOrder }) => enqueueRequest(() => reorderMenuItem({ itemId, newDisplayOrder })),

    onMutate: async ({ sectionId, fromIndex, toIndex }) => {
      const previous = queryClient.getQueryData<IMenu>(queryKey);

      const optimistic = previous
        ? queryClient.setQueryData<IMenu>(queryKey, {
            ...previous,
            sections: previous.sections.map((section) =>
              section.sectionId === sectionId
                ? { ...section, items: reassignDisplayOrders(reorderItems(section.items, fromIndex, toIndex)) }
                : section,
            ),
          })
        : undefined;

      await queryClient.cancelQueries({ queryKey }, { silent: true, revert: false });

      return { previous, optimistic };
    },

    onError: (_error, _variables, context) => {
      revertOptimisticWrite(queryClient, queryKey, context);
      queryClient.invalidateQueries({ queryKey });
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    },

    // Drags arrive in bursts; refetching the whole menu per drop is what makes
    // the existing move-left/right path too slow to reuse here.
    onSettled: (_data, error) => {
      clearTimeout(invalidateTimer.current);
      if (error) return;
      invalidateTimer.current = setTimeout(() => queryClient.invalidateQueries({ queryKey }), INVALIDATE_DEBOUNCE_MS);
    },
  });
};
